/**
 * Tela 21 — Auditoria (doc 09): consulta da trilha de `log_auditoria` com
 * filtro por entidade/registro/usuário/período, exibição de valor anterior ×
 * valor novo e origem da alteração. Aba adicional com o log de acesso
 * (doc 19 §4). Exportação CSV registrada no log de acesso (doc 19 §6).
 */
import { usePersonalizacao } from '../comercial/personalizacao.tsx';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, cabecalhosApi, consulta, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Aviso, Selo, Vazio } from '../componentes/basicos.tsx';
import { ROTULO_ACAO, ROTULO_EVENTO_ACESSO, dataHora, valorLog } from '../formatos.ts';

interface Filtros {
  entidade_tipo?: string;
  entidade_id?: string;
  usuario_id?: string;
  acao?: string;
  origem?: string;
  de?: string;
  ate?: string;
  pagina?: number;
}

const ROTULO_ENTIDADE: Record<string, string> = {
  usuario: 'Usuário',
  perfil: 'Perfil',
  permissao: 'Permissão',
  parametro: 'Parâmetro',
  plano_contas: 'Plano de contas',
  centro_custo: 'Centro de custo',
  conta_bancaria: 'Conta bancária',
  cliente: 'Cliente',
  cliente_entidade: 'Matriz/filial do Cliente',
  contato: 'Contato',
  parceiro: 'Parceiro comercial',
  parceiro_entidade: 'Entidade faturadora',
  produto: 'Produto ou serviço',
  produto_importacao: 'Importação de produtos',
  oportunidade: 'Oportunidade',
  oportunidade_contato: 'Contato da Oportunidade',
  registro_oportunidade: 'Registro da Oportunidade',
  atividade: 'Atividade',
  tarefa: 'Tarefa',
  lixeira_registro: 'Lixeira',
};

function filtrosDaRota(rota: string): Filtros {
  const q = new URLSearchParams(rota.split('?')[1] ?? '');
  return Object.fromEntries(q.entries()) as Filtros;
}

export function TelaAuditoria({ rota }: { rota: string }) {
  const { pode } = useSessao();
  const podeLog = pode('visualizar', 'log_auditoria');
  const [aba, setAba] = useState<'auditoria' | 'acessos'>(podeLog ? 'auditoria' : 'acessos');
  return (
    <section>
      <div className="abas" role="tablist">
        {podeLog && (
          <button role="tab" aria-selected={aba === 'auditoria'} className={aba === 'auditoria' ? 'ativa' : ''} onClick={() => setAba('auditoria')}>
            Trilha de auditoria
          </button>
        )}
        {pode('visualizar', 'log_acesso') && (
          <button role="tab" aria-selected={aba === 'acessos'} className={aba === 'acessos' ? 'ativa' : ''} onClick={() => setAba('acessos')}>
            Log de acesso
          </button>
        )}
      </div>
      {aba === 'auditoria' ? <TrilhaAuditoria key={rota} filtrosIniciais={filtrosDaRota(rota)} /> : <LogAcesso />}
    </section>
  );
}

function TrilhaAuditoria({ filtrosIniciais }: { filtrosIniciais: Filtros }) {
  const { pode } = useSessao();
  const [filtros, setFiltros] = useState<Filtros>(filtrosIniciais);
  // D7: colunas e visões da Auditoria (filtros = os do formulário, sem a página)
  const pers = usePersonalizacao('auditoria', { filtros: { ...filtros, pagina: undefined }, aplicarFiltros: (e: Record<string, unknown>) => setFiltros({ ...(e as Filtros), pagina: undefined }) });
  const [dados, setDados] = useState<{ itens: any[]; total: number; pagina: number; tamanho: number }>({ itens: [], total: 0, pagina: 1, tamanho: 50 });
  const [opcoes, setOpcoes] = useState<{ entidades: string[]; acoes: string[]; origens: string[] }>({ entidades: [], acoes: [], origens: [] });
  const [usuarios, setUsuarios] = useState<{ id: string; nome: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (f: Filtros) => {
    setErro(null);
    try {
      setDados(await api.get(`/api/auditoria${consulta({ ...f, tamanho: 50 } as Record<string, string | number | undefined>)}`));
    } catch (e) {
      setErro((e as ErroApi).message);
    }
  }, []);

  useEffect(() => {
    carregar(filtros);
  }, [carregar, filtros]);
  useEffect(() => {
    api.get('/api/auditoria/filtros').then(setOpcoes).catch(() => undefined);
    if (pode('visualizar', 'usuario')) api.get('/api/usuarios').then(setUsuarios).catch(() => undefined);
  }, [pode]);

  function aplicar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = Object.fromEntries([...new FormData(e.currentTarget).entries()].filter(([, v]) => v !== '')) as Filtros;
    setFiltros({ ...f, pagina: 1 });
  }

  async function exportar() {
    setErro(null);
    const res = await fetch(`/api/auditoria/exportar${consulta({ ...filtros, pagina: undefined } as Record<string, string | undefined>)}`, {
      headers: cabecalhosApi(), // CP11: a sessão vai no cookie HttpOnly
      credentials: 'same-origin',
    });
    if (!res.ok) {
      setErro((await res.json()).mensagem);
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const paginas = Math.max(1, Math.ceil(dados.total / dados.tamanho));

  return (
    <>
      <form className="filtros" onSubmit={aplicar} key={JSON.stringify(filtros) + opcoes.entidades.length + usuarios.length} aria-label="Filtros da auditoria">
        <label>
          Entidade
          <select name="entidade_tipo" defaultValue={filtros.entidade_tipo ?? ''}>
            <option value="">Todas</option>
            {opcoes.entidades.map((e) => (
              <option key={e} value={e}>
                {ROTULO_ENTIDADE[e] ?? e}
              </option>
            ))}
          </select>
        </label>
        <label>
          ID do registro
          <input name="entidade_id" defaultValue={filtros.entidade_id ?? ''} placeholder="UUID" pattern="[0-9a-fA-F-]{36}" />
        </label>
        <label>
          Usuário
          <select name="usuario_id" defaultValue={filtros.usuario_id ?? ''}>
            <option value="">Todos</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ação
          <select name="acao" defaultValue={filtros.acao ?? ''}>
            <option value="">Todas</option>
            {opcoes.acoes.map((a) => (
              <option key={a} value={a}>
                {ROTULO_ACAO[a] ?? a}
              </option>
            ))}
          </select>
        </label>
        <label>
          Origem
          <select name="origem" defaultValue={filtros.origem ?? ''}>
            <option value="">Todas</option>
            {opcoes.origens.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          De
          <input type="date" name="de" defaultValue={filtros.de ?? ''} />
        </label>
        <label>
          Até
          <input type="date" name="ate" defaultValue={filtros.ate ?? ''} />
        </label>
        <div className="filtros-acoes">
          <button className="botao-primario" type="submit">
            Filtrar
          </button>
          <button className="botao-secundario" type="button" onClick={() => setFiltros({})}>
            Limpar
          </button>
          {pode('exportar', 'log_auditoria') && (
            <button className="botao-secundario" type="button" onClick={exportar}>
              Exportar CSV
            </button>
          )}
        </div>
      </form>
      <Aviso>{erro}</Aviso>
      <div className="linha-personalizacao linha-resumo-auditoria">
        <p className="resumo sem-margem">{dados.total} registro(s) encontrado(s)</p>
        <span className="espaco" />
        {pers.controles}
      </div>
      {dados.itens.length === 0 ? (
        <Vazio>Nenhum registro de auditoria para os filtros informados.</Vazio>
      ) : (
        <div className="rolagem quadro-tabela">
        <table ref={pers.tabela} className="tabela tabela-auditoria">
          <thead>
            <tr>
              <th>Data/hora</th>
              <th>Usuário</th>
              <th>Entidade · registro</th>
              <th>Ação</th>
              <th>Campo</th>
              <th>Valor anterior</th>
              <th>Valor novo</th>
              <th>Origem</th>
              <th>Justificativa</th>
            </tr>
          </thead>
          <tbody>
            {dados.itens.map((l) => (
              <tr key={l.id}>
                <td className="nowrap">{dataHora(l.data_hora)}</td>
                <td>{l.usuario_nome ?? <em>sistema</em>}</td>
                <td>
                  <strong>{ROTULO_ENTIDADE[l.entidade_tipo] ?? l.entidade_tipo}</strong>
                  <br />
                  <button type="button" className="botao-link" title={l.entidade_id} onClick={() => setFiltros({ entidade_tipo: l.entidade_tipo, entidade_id: l.entidade_id })}>
                    {l.descricao_registro ?? l.entidade_id.slice(0, 8)}
                  </button>
                </td>
                <td>
                  <Selo tom="destaque">{ROTULO_ACAO[l.acao] ?? l.acao}</Selo>
                </td>
                <td>
                  <code>{l.campo ?? '—'}</code>
                </td>
                <td className="valor-antigo">{valorLog(l.valor_anterior)}</td>
                <td className="valor-novo">{valorLog(l.valor_novo)}</td>
                <td>{l.origem}</td>
                <td>{l.justificativa ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {paginas > 1 && (
        <div className="paginacao">
          <button className="botao-secundario pequeno" disabled={dados.pagina <= 1} onClick={() => setFiltros({ ...filtros, pagina: dados.pagina - 1 })}>
            Anterior
          </button>
          <span>
            Página {dados.pagina} de {paginas}
          </span>
          <button className="botao-secundario pequeno" disabled={dados.pagina >= paginas} onClick={() => setFiltros({ ...filtros, pagina: dados.pagina + 1 })}>
            Próxima
          </button>
        </div>
      )}
    </>
  );
}

const EVENTOS_ALERTA = [
  'login_falha', 'mfa_falha', 'login_bloqueado', 'acesso_negado',
  'provedor_login_falha', 'provedor_mfa_falha', 'provedor_ip_bloqueado', 'provedor_usuario_temporariamente_bloqueado', 'provedor_senha_vazada', 'provedor_lacuna_eventos',
];

function LogAcesso() {
  const [evento, setEvento] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<{ itens: any[]; total: number; tamanho: number }>({ itens: [], total: 0, tamanho: 50 });
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    api
      .get(`/api/acessos${consulta({ evento, pagina, tamanho: 50 })}`)
      .then(setDados)
      .catch((e) => setErro(e.message));
  }, [evento, pagina]);
  const paginas = Math.max(1, Math.ceil(dados.total / dados.tamanho));
  return (
    <>
      <div className="barra-ferramentas">
        <select value={evento} onChange={(e) => (setEvento(e.target.value), setPagina(1))} aria-label="Filtrar por evento">
          <option value="">Todos os eventos</option>
          {Object.entries(ROTULO_EVENTO_ACESSO).map(([v, r]) => (
            <option key={v} value={v}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <Aviso>{erro}</Aviso>
      <table className="tabela">
        <thead>
          <tr>
            <th>Ocorrido em</th>
            <th>Evento</th>
            <th>Usuário / e-mail informado</th>
            <th>IP</th>
            <th>Detalhe</th>
          </tr>
        </thead>
        <tbody>
          {dados.itens.map((a) => (
            <tr key={a.id}>
              <td className="nowrap">
                {dataHora(a.data_hora)}
                {/* CP13b: evento do provedor importado depois — a data principal é a da ocorrência */}
                {a.detalhe?.importado_em && <div className="texto-secundario">importado em {dataHora(a.detalhe.importado_em)}</div>}
              </td>
              <td>
                <Selo tom={EVENTOS_ALERTA.includes(a.evento) ? 'alerta' : 'neutro'}>
                  {ROTULO_EVENTO_ACESSO[a.evento] ?? a.evento}
                </Selo>
              </td>
              <td>{a.usuario_nome ?? a.email_informado ?? '—'}</td>
              <td>{a.ip ?? '—'}</td>
              <td>
                <code className="detalhe">{a.detalhe ? JSON.stringify(a.detalhe) : '—'}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {paginas > 1 && (
        <div className="paginacao">
          <button className="botao-secundario pequeno" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
            Anterior
          </button>
          <span>
            Página {pagina} de {paginas}
          </span>
          <button className="botao-secundario pequeno" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>
            Próxima
          </button>
        </div>
      )}
    </>
  );
}
